import React, { memo, useMemo, useCallback } from 'react';
import cn from 'classnames';
import style from './showNotification.module.scss';
import Loader from '@components/common/Loader/newLoaderModal';
import { CUSTOM_MESSAGES, LABEL } from '@veriables';
import { TouchableButton } from '@components/common/Touchable';
import FullWidthModal from '@components/common/Modal/FullWidthModal';
import NotificationRow from './NotificationItem';

const checkReboot = () => {
  const lastRebootOn = window.sessionStorage.getItem('lastRebootOn');
  if (
    !lastRebootOn ||
    lastRebootOn === 'null' ||
    lastRebootOn === 'undefined'
  ) {
    return false;
  }
  const dt = new Date();
  const dt2 = new Date(lastRebootOn);
  return (
    dt.getFullYear() !== dt2.getFullYear() ||
    dt.getMonth() !== dt2.getMonth() ||
    dt.getDate() !== dt2.getDate()
  );
};

const ShowNotificationModal = ({
  showNotification,
  setShowNotification,
  serverAppTheme,
  isLandScape,
  notificationTableData,
  notifyLoading,
  removeStoreNotification,
  acknowledge,
  storeId,
}) => {
    
  const popupStyles = useMemo(
    () => ({
      fullWithModal: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        pointerEvents: 'auto',
        backgroundColor: serverAppTheme?.popUp_bg,
        height: '100%',
        borderRadius: '16px',
        border: serverAppTheme?.popupBorder,
        boxShadow: serverAppTheme?.popUp_box_shadow,
        color: serverAppTheme?.primary_text
      },
      parentContainerColor: { color: serverAppTheme?.primary_text },
      notificationStyle: {
        background: serverAppTheme?.OFF_Premise_Btn,
        border: serverAppTheme?.notification_box_border,
      },
      notificationColor: { color: serverAppTheme?.notification_time_font },
      dismissStyle: {
        backgroundColor: serverAppTheme?.OFF_Premise_Btn,
        border: serverAppTheme?.cancleBtn_Border,
        color: serverAppTheme?.cancleBtn_font,
      },
      acknowledgedStyle: {
        backgroundColor: serverAppTheme?.primaryBtn,
        color: serverAppTheme?.btn_font_color,
      },
      norecord: { color: serverAppTheme?.noRecord },
      closebtn: {
        borderRadius: '4px',
        backgroundColor: serverAppTheme?.secondaryBtn,
        color: serverAppTheme?.btn_font_color,
        width: '17.5rem',
      },
      closeButton: {
        backgroundColor: serverAppTheme?.OFF_Premise_Btn,
        border: serverAppTheme?.cancleBtn_Border,
        color: serverAppTheme?.cancleBtn_font,
      },
    }),
    [serverAppTheme]
  );

  const validItemIds = useMemo(
    () => notificationTableData.filter(item => !Array.isArray(item)).map(item => item.id),
    [notificationTableData]
  );

  const handleDismiss = useCallback(
    itemId => {
      removeStoreNotification(itemId);
    },
    [removeStoreNotification]
  );

  const handleAcknowledge = useCallback(
    itemId => {
      acknowledge({ storeId, notificationId: itemId }).then(() => {
        removeStoreNotification(itemId);
      });
    },
    [acknowledge, removeStoreNotification, storeId]
  );

  const handleClose = useCallback(
    e => {
      setShowNotification(prev => ({ ...prev, state: false }));
      e.preventDefault();
    },
    [setShowNotification]
  );

  const listStyle = useMemo(
    () => ({
      maxHeight: isLandScape ? '80vh' : '75vh',
      overflowY: 'auto',
    }),
    [isLandScape]
  );

  const hasReboot = checkReboot();
  const hasItems = notificationTableData?.length > 0;
  const title = hasItems
    ? `${LABEL().NS_NOTIFICATIONS} (${showNotification.count} new)`
    : `${LABEL().NS_NOTIFICATIONS} (${showNotification.count})`;

  return (
    <FullWidthModal
      isOpen={showNotification.state}
      isModalCenter={true}
      customClass={cn('h-100')}
      customClass2="h-100 w-100"
      customWidth={true}
      style={{ backgroundColor: serverAppTheme?.popUp_bg, height: '100%' }}
    >
      <div
        className="h-100 justify-content-center align-items-center text-center"
        style={popupStyles.parentContainerColor}
      >
        <div className="col-12 p-0 d-flex text-right">
          <div
            className={cn('col-12 text-center fw-600 fs_18', style.moretitlebg)}
          >
            {title}
          </div>
          <div
            className={cn(
              'text-right h-auto position-fixed',
              style.modalPosition
            )}
          />
        </div>

        <div className={cn('col-12 px-0 h-95')}>
          {notifyLoading && <Loader />}
          {hasReboot && (
            <div className="pt-1">
              <div className={cn('p-2 text-primary', style.rebootStyle)}>
                {CUSTOM_MESSAGES().TABLET_REQUIRES_A_REBOOT ||
                  'TABLET REQUIRES A REBOOT. PLEASE HOLD THE POWER BUTTON AND CLICK ON RESTART'}
              </div>
            </div>
          )}

          <div
            className={cn(
              'mt-3',
              hasItems && isLandScape
                ? 'px-3'
                : 'px-12 h-90 d-flex align-items-center justify-content-center'
            )}
          >
            {hasItems ? (
              <div style={listStyle}>
                {validItemIds.map(id => (
                  <NotificationRow
                    key={id}
                    id={id}
                    isLandScape={isLandScape}
                    notificationStyle={popupStyles.notificationStyle}
                    dismissStyle={popupStyles.dismissStyle}
                    notificationColor={popupStyles.notificationColor}
                    acknowledgedStyle={popupStyles.acknowledgedStyle}
                    onDismiss={handleDismiss}
                    onAcknowledge={handleAcknowledge}
                  />
                ))}
              </div>
            ) : (
              <div
                className={cn(
                  'col-12 h-95 pt-3 pb-0 px-3 row justify-content-center align-items-center fs_18 fw-400 mx-0'
                )}
                style={popupStyles.norecord}
              >
                {CUSTOM_MESSAGES().NS_NO_NOTIFICATION}
              </div>
            )}
          </div>
        </div>

        {notificationTableData?.length >= 0 && (
          <div className={style.closeBtn}>
            <TouchableButton
              className={cn(
                'col-5 m-2 mb-3 btn-ripple p-0',
                isLandScape ? 'btn44' : 'btn68'
              )}
              style={popupStyles.closeButton}
              type="button"
              onClick={handleClose}
              rippleEffect={true}
            >
              {LABEL().NS_CLOSE}
            </TouchableButton>
          </div>
        )}
      </div>
    </FullWidthModal>
  );
};

export default memo(ShowNotificationModal);
